import React, { useState, useEffect } from 'react'
import { ServerConfig } from '@/types'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { toastError, toastSuccess } from '@/utils/toast'

interface ServerConfigSectionProps {
  initialConfig?: ServerConfig
  onSave: (config: ServerConfig) => Promise<void>
  readOnly?: boolean
}

export const ServerConfigSection: React.FC<ServerConfigSectionProps> = ({
  initialConfig,
  onSave,
  readOnly = false,
}) => {
  const [serverConfig, setServerConfig] = useState<ServerConfig>({
    updateServerUrl: '',
    updateServerPort: undefined,
    useTls: false,
  })

  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (initialConfig) {
      setServerConfig(initialConfig)
    }
  }, [initialConfig])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setServerConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }))
  }

  const handleSubmit = async () => {
    setIsSaving(true)
    try {
      await onSave(serverConfig)
      toastSuccess('Configuration du serveur mise à jour avec succès')
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Erreur lors de la mise à jour de la configuration du serveur'
      toastError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
      <div className="space-y-4">
        <Input
          label="URL du serveur de mise à jour"
          type="text"
          name="updateServerUrl"
          value={serverConfig.updateServerUrl || ''}
          onChange={handleChange}
          placeholder="https://example.com"
          disabled={readOnly}
        />
        <Input
          label="Port du serveur de mise à jour"
          type="number"
          name="updateServerPort"
          value={serverConfig.updateServerPort || ''}
          onChange={handleChange}
          placeholder="443"
          disabled={readOnly}
        />
        <Checkbox
          label="Utiliser TLS"
          name="useTls"
          checked={serverConfig.useTls || false}
          onChange={handleChange}
          disabled={readOnly}
        />
        {!readOnly && (
          <div className="flex justify-end">
            <Button onClick={handleSubmit} isLoading={isSaving}>
              Enregistrer la configuration
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
